<template>
	<div class="results-toolbar results-toolbar--variant-three">
		<ResultsToolbar>
			<template #left-container-above>
				<Search
					:placeholder="placeholder"
					v-model.lazy="filters.search"
					:timeout="300"
					:width="searchWidth"
					@change="emitValues"
				/>
			</template>
			<template #left-container-below>
				<span
					@mouseover="tooltipMoveToVisible = true"
					@mouseleave="tooltipMoveToVisible = false"
					ref="menuButtonV2Ref"
					class="tooltip-container"
				>
					<MenuButtonV2
						:options="bulkActions"
						placeholder="Mark as"
						@selected="onMenuOptionSelected"
						:disabled="disabledMarkButton"
						:width="'90px'"
					/>
					<Tooltip
						:text="
							'Select at least one error to enable bulk actions'
						"
						:display="displayTooltip"
						:trackedRefName="'menuButtonV2Ref'"
						position="auto"
						:offset="[0, 6]"
					/>
				</span>
			</template>
			<template #right-reset-btn>
				<Button
					v-if="showResetFilters"
					class="results-toolbar--error-logs__reset-filters"
					className="btn--link"
					labelText="Restore Filters"
					:btnClick="resetFilters"
				/>
			</template>
			<template #right-desk-container>
				<DatePicker
					v-model="parseDate"
					@change="emitValues"
					:width="185"
					:disableDaysAfterMaxDate="disableDaysAfterMaxDate"
					:disableDaysBeforeStartDate="disableDaysBeforeStartDate"
					:minDate="datePickerMinDate"
					:maxDate="datePickerMaxDate"
					:startDate="startDate"
				/>
				<DropdownV2
					name="users"
					v-model="filters.userId"
					:options="userOptions"
					:showClearSelectedButton="true"
					placeholder="All Users"
					dropdownWidth="245px"
					@search="$emit('search-users', $event)"
					@clean-search="$emit('clean-search', $event)"
					@change="emitValues"
					ref="usersDropdownRef"
				/>
				<DropdownV2
					v-model="filters.isResolved"
					placeholder="All Statuses"
					:options="statusOptions"
					:defaultValue="false"
					:dropdownWidth="'148px'"
					@change="emitValues"
				/>
			</template>
			<template #right-mobile-container>
				<Button
					className="btn--primary"
					iconClassName="icon-filter"
					:labelText="filterLabel"
					:iconBeforeLabel="true"
					:btnClick="
						() => {
							displayFiltersModal = true;
						}
					"
				/>
			</template>
			<template #counter> <ResultCounter :counter="counter"/></template>
		</ResultsToolbar>

		<Modal
			:display="displayFiltersModal"
			title="Filters"
			@show="displayFiltersModal = false"
		>
			<template #modal-content>
				<div class="results-toolbar--modal-filters">
					<DatePicker
						v-model="parseDate"
						:class="'date-picker__container'"
						:disableDaysAfterMaxDate="disableDaysAfterMaxDate"
						:disableDaysBeforeStartDate="disableDaysBeforeStartDate"
						:minDate="datePickerMinDate"
						:maxDate="datePickerMaxDate"
						:startDate="startDate"
					/>
					<DropdownV2
						name="users"
						v-model="filters.userId"
						:options="userOptions"
						:showClearSelectedButton="true"
						placeholder="All Users"
						dropdownWidth="300px"
						@search="$emit('search-users', $event)"
						@clean-search="$emit('clean-search', $event)"
						ref="usersDropdownRef"
					/>
					<DropdownV2
						v-model="filters.isResolved"
						placeholder="All Statuses"
						:options="statusOptions"
						:dropdownWidth="'300px'"
					/>
				</div>
			</template>
			<template #modal-footer>
				<ButtonGroup>
					<template #container>
						<Button
							labelText="Apply"
							className="btn--primary"
							:btnClick="applyFilterUsingModal"
							:disabled="!filtersModalHaveChanges"
						/>
						<Button
							labelText="Cancel"
							className="btn--link"
							:btnClick="closeModalFilters"
						/>
					</template>
				</ButtonGroup>
			</template>
		</Modal>
	</div>
</template>

<script>
import Search from '@/components/forms/search/search';
import Button from '@/components/forms/button/button';
import ButtonGroup from '@/components/forms/button/button-group';
import MenuButtonV2 from '@/components/forms/button/menu-button-v2';
import DatePicker from '@/components/forms/date-picker/date-picker';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Modal from '@/components/shells/modal/modals';
import ResultsToolbar from '@/components/shells/results-toolbar/results-toolbar.vue';
import Tooltip from '@/components/shells/tooltip/tooltip';

// libraries
import { countsDiffObjects } from '@/utils/function';
import { formatDate } from '@/utils/datetime';
import { deepEqualNoFn } from '@/utils/object';
import { parse } from 'date-fns';
import _ from 'lodash';

export default {
	name: 'ErrorLogsFilters',
	components: {
		Search,
		Button,
		ButtonGroup,
		MenuButtonV2,
		DatePicker,
		DropdownV2,
		ResultCounter,
		Modal,
		ResultsToolbar,
		Tooltip
	},
	props: {
		value: {
			type: Object,
			default: () => {}
		},
		defaultFilters: { type: Object, default: () => {} },
		counter: {
			type: Number
		},
		userOptions: { type: Array, default: () => [] },
		statusOptions: { type: Array, default: () => [] },
		placeholder: {
			type: String,
			default: 'Search error log details'
		},
		datePickerMinDate: {
			type: Date
		},
		datePickerMaxDate: {
			type: Date
		},
		datePickerWidth: {
			type: [String, Number],
			default: null
		},
		disableDaysAfterMaxDate: {
			type: Boolean,
			default: false
		},
		disableDaysBeforeStartDate: {
			type: Boolean,
			default: false
		},
		startDate: {
			type: Date
		},
		searchWidth: {
			type: Number,
			default: null
		},
		markButtonOpts: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			displayFiltersModal: false,
			filters: {},
			currentDefaultFilters: {
				...this.value
			},
			keysToPick: ['minDatetime', 'maxDatetime', 'userId', 'isResolved'],

			tooltipMoveToVisible: false
		};
	},
	created() {
		this.updateLocalValues(this.value);
	},
	computed: {
		filtersModalHaveChanges() {
			return !deepEqualNoFn(this.filters, this.value, true);
		},
		showResetFilters() {
			return !deepEqualNoFn(
				this.filteredFilters,
				this.filteredCurrentDefaultFilters,
				true
			);
		},
		filteredFilters() {
			return _.pick(this.filters, this.keysToPick);
		},
		filteredCurrentDefaultFilters() {
			return _.pick(this.defaultFilters, this.keysToPick);
		},
		totalFiltersNumber() {
			return countsDiffObjects(
				this.filteredCurrentDefaultFilters,
				this.filteredFilters
			);
		},
		filterLabel() {
			if (this.totalFiltersNumber) {
				return ` Filters (${this.totalFiltersNumber})`;
			}
			return ' Filters';
		},
		parseDate: {
			get() {
				if (this.filters.minDatetime) {
					return {
						startDate: parse(
							this.filters.minDatetime,
							'MM-dd-yyyy',
							new Date()
						),
						endDate: parse(
							this.filters.maxDatetime,
							'MM-dd-yyyy',
							new Date()
						)
					};
				} else {
					return {
						startDate: parse(
							this.defaultFilters.minDatetime,
							'MM-dd-yyyy',
							new Date()
						),
						endDate: parse(
							this.defaultFilters.maxDatetime,
							'MM-dd-yyyy',
							new Date()
						)
					};
				}
			},
			set(value) {
				if (value.startDate) {
					this.filters.minDatetime = formatDate(value.startDate);
					this.filters.maxDatetime = formatDate(value.endDate);
				} else {
					this.filters.minDatetime = this.defaultFilters.minDatetime;
					this.filters.maxDatetime = this.defaultFilters.maxDatetime;
				}
			}
		},
		bulkActions() {
			return this.markButtonOpts?.bulkActions;
		},
		disabledMarkButton() {
			return this.markButtonOpts?.disabled;
		},
		displayTooltip() {
			return (
				this.disabledMarkButton &&
				this.tooltipMoveToVisible &&
				!this.selectedIds?.length
			);
		}
	},
	methods: {
		updateLocalValues(obj) {
			this.filters = _.cloneDeep(obj);
		},
		applyFilterUsingModal() {
			this.displayFiltersModal = false;
			this.$emit('input', { ...this.filters });
		},
		resetFilters() {
			const resetFilters = {
				...this.filters,
				...this.filteredCurrentDefaultFilters
			};
			this.updateLocalValues(resetFilters);
			this.currentDefaultFilters = _.cloneDeep(this.filters);
			this.cleanSearchOfUserDropdown();

			this.$emit('input', { ...this.filters });
			this.$emit('reset-filters');
		},
		cleanSearchOfUserDropdown() {
			const dropdownListRef = this.$refs.usersDropdownRef?.$refs
				?.dropdownListRef;

			if (dropdownListRef) {
				this.$set(dropdownListRef, 'searchQuery', '');
			}
		},
		closeModalFilters() {
			this.filters = _.cloneDeep(this.value);
			this.displayFiltersModal = false;
		},
		emitValues() {
			this.$emit('input', { ...this.filters });
			this.$emit('resetSelectedIds', true);
		},
		onMenuOptionSelected(option) {
			this.$emit('optionSelected', option);
		}
	},
	watch: {
		value(newValue) {
			this.updateLocalValues(newValue);
		}
	}
};
</script>

//
<style lang="scss" scoped>
::v-deep .modal__body {
	display: flex;
	gap: $gutter-md;
}

.results-toolbar--modal-filters {
	display: grid;
	gap: $gutter-lg;
}

.date-picker__container {
	width: 300px;
}

.results-toolbar--error-logs {
	&__reset-filters {
		margin-left: auto;
	}

	&__mobile {
		margin-left: $gutter-lg;

		@include mq($from: md) {
			margin-left: 0;
		}

		@include mq($from: md-l) {
			display: none;
		}
	}

	&__desktop {
		display: none;
		z-index: 100;

		@include mq($from: md-l) {
			display: flex;
			gap: $gutter-lg;
		}
	}
}

.tooltip-container {
	position: relative;

	.tooltip {
		position: absolute;
		top: 50%;
		left: 100%;
		transform: translateY(-50%);
		z-index: 1000;
		margin-left: 10px;
	}
}
</style>
